import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { toast } from 'react-toastify';
import axios from 'axios';
import { AuthLayout } from './AuthLayout';
import { SeoHead } from '../common/SeoHead';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success(t('auth.loginSuccess'));
      navigate('/');
    } catch (error: any) {
      console.error('Login error:', error.code);
      toast.error(
        error.code === 'auth/invalid-credential'
          ? t('errors.auth')
          : error.message || t('errors.auth')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email.trim()) {
      toast.error(t('auth.email'));
      return;
    }

    setResetLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        email: email.trim(),
        locale: i18n.language,
      });
      toast.success(
        t('auth.resetPasswordEmailSent', 'Password reset email sent')
      );
      setResetOpen(false);
    } catch (error: any) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        t('errors.auth');
      toast.error(msg);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <SeoHead page="login" />
      <AuthLayout>
        <div className="mx-auto w-full max-w-md">
          <h1 className="text-2xl font-bold text-center mb-6">
            {t('auth.login')}
          </h1>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('auth.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input"
                placeholder="••••••••"
              />
            </div>
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => setResetOpen((v) => !v)}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                {t('auth.forgotPassword', 'Forgot password?')}
              </button>
            </div>
            {resetOpen && (
              <div className="space-y-3 rounded-md bg-gray-50 p-4">
                <p className="text-sm text-gray-700">
                  {t(
                    'auth.resetPasswordHint',
                    'Enter your email and we will send a password reset link'
                  )}
                </p>
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={resetLoading}
                  className="btn btn-secondary w-full"
                >
                  {resetLoading
                    ? t('common.loading')
                    : t('auth.sendResetLink', 'Send reset link')}
                </button>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              {loading ? t('common.loading') : t('auth.login')}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-gray-600">
            <Link
              to="/register"
              className="text-primary-600 hover:text-primary-700"
            >
              {t('auth.register')}
            </Link>
          </p>
        </div>
      </AuthLayout>
    </>
  );
};
